import React, { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getDictionaryStats,
  callWithAuth,
  completeTutorialItem,
} from "../api/Api.ts";
import { PageContainer } from "@/ui";
import DictionaryHero from "../components/Dictionary/DictionaryHero.tsx";
import DictionaryList from "../components/Dictionary/DictionaryList.tsx";
import AppBackground from "../components/AppBackground.tsx";
import { ContentType } from "../components/Dictionary/WordsPhrasesTabs.tsx";
import { useTrackViewedWords } from "../hooks/posthog/useTrackViewedWords.tsx";
import { useTrackViewedPhrases } from "../hooks/posthog/useTrackViewedPhrases.tsx";
import { useTrackDictionaryEvents } from "../hooks/posthog/useTrackDictionaryEvents.tsx";
import { LoadingPage } from "./LoadingPage.tsx";

const DICTIONARY_STATS_QUERY_KEY = "dictionaryStats";
const KNOWN_PHRASES_QUERY_KEY = "knownPhrases";
const LEARNING_PHRASES_QUERY_KEY = "learningPhrases";

type ViewMode = "known" | "learning";

const DictionaryPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const trackViewedWords = useTrackViewedWords();
  const trackViewedPhrases = useTrackViewedPhrases();
  const {
    trackDictionaryViewed,
    trackDictionaryViewModeChanged,
  } = useTrackDictionaryEvents();
  const queryClient = useQueryClient();

  // iOS fires dictionary_viewed on focus (Dictionary.tsx:140); web has no tab
  // focus, so mounting the page is the equivalent view.
  useEffect(() => {
    trackDictionaryViewed();
  }, [trackDictionaryViewed]);

  // iOS default: Words / Learning. Deep-link via ?contentType=&viewMode=.
  const paramContent = searchParams.get("contentType");
  const paramMode = searchParams.get("viewMode");
  // Deep-link from the locked-feed Review CTA / tutorial: pulse-highlight the
  // review button in the hero. Mirrors client-native Dictionary highlightReview.
  const highlightReview = searchParams.get("highlightReview") === "true";
  // Deep-link from the tutorial checklist ("Review your dictionary"): complete
  // the review_dictionary item once per mount and scroll down to hide the
  // insights box. Mirrors client-native Dictionary.tsx:153-164.
  const fromTutorial = searchParams.get("fromTutorial") === "true";
  // The URL is the single source of truth (so toast "View" deep-links that
  // change params in place are reflected without a state-sync effect — see
  // https://react.dev/learn/you-might-not-need-an-effect). UI changes push the
  // new value into the URL via setSearchParams below.
  const contentType: ContentType = paramContent === "phrases" ? "phrases" : "words";
  const viewMode: ViewMode = paramMode === "known" ? "known" : "learning";

  // iOS completes the review_dictionary tutorial item (once per session) when
  // arriving from the tutorial, then scrolls down 200px to hide the insights
  // box (Dictionary.tsx:153-164).
  const tutorialCompletedRef = useRef(false);
  useEffect(() => {
    if (!fromTutorial || tutorialCompletedRef.current) return;
    tutorialCompletedRef.current = true;
    completeTutorialItem("review_dictionary").catch((error) => {
      console.error("Error completing tutorial item:", error);
    });
    const timeout = setTimeout(() => {
      window.scrollTo({ top: 200, behavior: "smooth" });
    }, 300);
    return () => clearTimeout(timeout);
  }, [fromTutorial]);

  // iOS scrolls the list to the top when highlighting the review button so the
  // pulsing hero is in view (Dictionary.tsx highlightReview branch, 300ms).
  useEffect(() => {
    if (!highlightReview) return;
    const timeout = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 300);
    return () => clearTimeout(timeout);
  }, [highlightReview]);

  const dictionaryStatsQuery = useQuery({
    queryKey: [DICTIONARY_STATS_QUERY_KEY],
    queryFn: () => callWithAuth(getDictionaryStats),
  });

  if (dictionaryStatsQuery.isPending) {
    // Paint the sky background (same scene as the loaded page) behind the
    // spinner so the load doesn't flash a bare nav bar over a blank area — the
    // words/stats fill in once the query resolves. Mirrors the loaded + error
    // branches, which both render AppBackground first.
    return (
      <>
        <AppBackground variant="fade" />
        <PageContainer padded={false}>
          <div className="relative z-[1] w-full min-h-full">
            <LoadingPage showText={false} />
          </div>
        </PageContainer>
      </>
    );
  }

  // Surface loading/error instead of rendering fabricated zeroed stats (which
  // would downgrade the review button to "locked" for already-unlocked users).
  // Mirrors LearnPage's "Couldn't load your progress" + Retry view.
  if (dictionaryStatsQuery.isError) {
    return (
      <>
        <AppBackground variant="fade" />
        <PageContainer padded={false}>
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              minHeight: "100%",
              padding: 24,
              textAlign: "center",
            }}
          >
            <span
              style={{
                fontSize: "var(--base-font-size-large)",
                fontWeight: 600,
              }}
            >
              Couldn't load your progress
            </span>
            <button
              type="button"
              onClick={() => dictionaryStatsQuery.refetch()}
              style={{
                padding: "10px 24px",
                borderRadius: 999,
                background: "var(--color-blue)",
                color: "#fff",
                border: "none",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              Retry
            </button>
          </div>
        </PageContainer>
      </>
    );
  }

  const dictionaryStats = dictionaryStatsQuery.data || {
    knownWordCount: 0,
    learningWordCount: 0,
    knownPhraseCount: 0,
    learningPhraseCount: 0,
  };

  const handleContentTypeChange = (type: ContentType) => {
    setSearchParams(
      (prev) => {
        prev.set("contentType", type);
        return prev;
      },
      { replace: true },
    );
    if (type === "words") trackViewedWords();
    else trackViewedPhrases();
  };

  const handleStatusUpdate = () => {
    queryClient.invalidateQueries({ queryKey: [DICTIONARY_STATS_QUERY_KEY] });
  };

  return (
    // Blue sky + sun + clouds + grass behind the content (matches iOS Dictionary,
    // which renders the same scene). Only Courses is a solid background.
    <>
      <AppBackground variant="fade" />
      <PageContainer padded={false}>
        <div className="relative z-[1] flex flex-col justify-start w-full min-h-full pb-8 pt-14">
          <DictionaryHero highlightReview={highlightReview} />
          <DictionaryList
            contentType={contentType}
            onContentTypeChange={handleContentTypeChange}
            viewMode={viewMode}
            onViewModeChange={(mode) => {
              trackDictionaryViewModeChanged(contentType, mode);
              setSearchParams(
                (prev) => {
                  prev.set("viewMode", mode);
                  return prev;
                },
                { replace: true },
              );
            }}
            onStatusUpdate={handleStatusUpdate}
            phraseQueryKeys={{
              knownPhrasesKey: KNOWN_PHRASES_QUERY_KEY,
              learningPhrasesKey: LEARNING_PHRASES_QUERY_KEY,
            }}
            dictionaryStats={dictionaryStats}
          />
        </div>
      </PageContainer>
    </>
  );
};

export default DictionaryPage;
